/**
 * ReviewChangesDialog Component (ORCH-336, ORCH-410; PRD User Manager UM-9)
 *
 * Purpose: The review step before saving a person's changes: every role and
 *          attribute value being added or removed, and every profile field that
 *          actually changed, by its own label with the value before and after
 *          ("Country: Not set → France"), so an admin sees exactly what they are
 *          about to change. The title is built here, once, as
 *          "Save changes to {name}?" so every caller words it the same way.
 * Dependencies: MUI Dialog, LoadingButton
 */

import React from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { LoadingButton } from '../common';
import { MembershipChange } from './membership';
import { SecondaryButton } from '../common/ActionButtons';

export interface ReviewChangesDialogProps {
  open: boolean;
  /** Whose changes these are, as the title names them: "Save changes to Maria Lopez?". */
  personName: string;
  changes: MembershipChange[];
  saving?: boolean;
  onSave: () => void;
  onClose: () => void;
}

/** The dialog's title for a person, the one wording every caller gets. */
export const reviewChangesTitle = (personName: string): string => `Save changes to ${personName}?`;

const MARKS: Record<MembershipChange['kind'], { mark: string; color: string; verb: string }> = {
  added: { mark: '+', color: 'success.main', verb: 'Add ' },
  removed: { mark: '−', color: 'error.main', verb: 'Remove ' },
  updated: { mark: '•', color: 'text.secondary', verb: '' },
};

const ReviewChangesDialog: React.FC<ReviewChangesDialogProps> = ({
  open,
  personName,
  changes,
  saving = false,
  onSave,
  onClose,
}) => (
  <Dialog
    open={open}
    onClose={saving ? undefined : onClose}
    maxWidth="sm"
    fullWidth
    data-testid="review-changes-dialog"
  >
    <DialogTitle>{reviewChangesTitle(personName)}</DialogTitle>
    <DialogContent>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {changes.map((change, index) => (
          <Box
            key={`${change.kind}-${change.label}-${change.value}-${index}`}
            data-testid={`review-change-${change.kind}`}
            sx={{
              display: 'flex',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color={MARKS[change.kind].color}
              aria-hidden
            >
              {MARKS[change.kind].mark}
            </Typography>
            <Typography variant="body2">
              {MARKS[change.kind].verb}
              {change.label}:{' '}
              {change.kind === 'updated' ? (
                <>
                  {change.before} → <b>{change.value}</b>
                </>
              ) : (
                <b>{change.value}</b>
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </DialogContent>
    <DialogActions>
      <SecondaryButton onClick={onClose} disabled={saving} data-testid="review-changes-back">
        Back
      </SecondaryButton>
      <LoadingButton
        variant="contained"
        loading={saving}
        onClick={onSave}
        data-testid="review-changes-save"
      >
        Save
      </LoadingButton>
    </DialogActions>
  </Dialog>
);

export default ReviewChangesDialog;
